import { PREFIX_COUNTRIES } from './prefix-countries.data';

const E164_PATTERN = /^\+[1-9]\d{7,14}$/;
const NATIONAL_NUMBER_PATTERN = /^[0-9]{6,14}$/;

const KNOWN_DIAL_PREFIXES = [...new Set(PREFIX_COUNTRIES.map((country) => country.dialCode))].sort(
	(a, b) => b.length - a.length,
);

export function normalizeNationalNumber(value: string | null | undefined): string {
	return String(value ?? '')
		.replace(/\D/g, '')
		.trim();
}

export function normalizeDialPrefix(value: string | ISelectLike | null | undefined): string {
	if (!value) {
		return '';
	}
	if (typeof value === 'object') {
		if (value.value?.dialCode) {
			return normalizeDialPrefix(String(value.value.dialCode));
		}
		if (typeof value.id === 'string' && value.id.startsWith('+')) {
			return normalizeDialPrefix(value.id);
		}
		return '';
	}
	if (typeof value === 'string' && value.startsWith('+')) {
		return value.startsWith('+') ? value : `+${value.replace(/\D/g, '')}`;
	}
	return '';
}

interface ISelectLike {
	id?: string | number;
	value?: {
		dialCode?: string;
	};
}

export function toE164(prefix: string | ISelectLike | null | undefined, number: string | null | undefined): string | null {
	const normalizedPrefix = normalizeDialPrefix(prefix);
	const normalizedNumber = normalizeNationalNumber(number);
	if (!normalizedPrefix || !normalizedNumber) {
		return null;
	}
	const e164 = `${normalizedPrefix}${normalizedNumber}`;
	return isValidE164(e164) ? e164 : null;
}

export function isValidE164(value: string | null | undefined): boolean {
	if (!value) {
		return false;
	}
	const normalized = value.startsWith('+') ? value : `+${value}`;
	return E164_PATTERN.test(normalized);
}

export function isValidNationalNumber(value: string | null | undefined): boolean {
	return NATIONAL_NUMBER_PATTERN.test(normalizeNationalNumber(value));
}

export function parseE164(e164: string | null | undefined): { prefix: string; number: string } | null {
	if (!e164) {
		return null;
	}
	const normalized = e164.startsWith('+') ? e164.trim() : `+${e164.trim()}`;
	if (!normalized.startsWith('+')) {
		return null;
	}
	for (const prefix of KNOWN_DIAL_PREFIXES) {
		if (normalized.startsWith(prefix)) {
			const number = normalized.slice(prefix.length);
			if (!number || !/^\d+$/.test(number)) {
				return null;
			}
			return { prefix, number };
		}
	}
	return null;
}

export function splitForWorkLife(e164: string | null | undefined): { prefix: string; phone: string } | null {
	const parsed = parseE164(e164);
	if (!parsed) {
		return null;
	}
	return { prefix: parsed.prefix, phone: parsed.number };
}

export function maskPhoneNumber(nationalNumber: string | null | undefined): string {
	const digits = normalizeNationalNumber(nationalNumber);
	if (digits.length < 4) {
		return digits;
	}
	return `${digits.slice(0, 2)}****${digits.slice(-3)}`;
}
